import { useState, useEffect, useMemo } from 'react'
import { useProviderProfile } from '@/hooks/useProfile'
import { useConfigureProfile } from '@/hooks/useProviderConfiguration'
import { useDistricts } from '@/hooks/useServices'
import Input from '@/components/forms/Input/Input'
import Button from '@/components/common/Button/Button'
import toast from 'react-hot-toast'
import './Steps.css'

interface LocationStepProps {
    onComplete?: () => void
}

type District = {
    id: number
    name: string
    price_multiplier?: number
}

const LocationStep = ({ onComplete }: LocationStepProps) => {
    const { data: providerData, isLoading: providerLoading } = useProviderProfile()
    const configureProfile = useConfigureProfile()
    const { data: districts = [], isLoading: districtsLoading } = useDistricts()

    const [selectedDistricts, setSelectedDistricts] = useState<number[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (providerData) {
            const existingDistricts = Array.isArray(providerData.service_districts)
                ? providerData.service_districts
                      .map((district: any) => (typeof district === 'number' ? district : district?.id))
                      .filter((id: number | undefined): id is number => typeof id === 'number')
                : []

            setSelectedDistricts(existingDistricts)
        }
    }, [providerData])

    const normalizedDistricts = useMemo(() => {
        const list = Array.isArray(districts) ? (districts as District[]) : []
        return list.slice().sort((a, b) => {
            const multiplierA = Number(a.price_multiplier ?? 1)
            const multiplierB = Number(b.price_multiplier ?? 1)
            if (multiplierA !== multiplierB) {
                return multiplierB - multiplierA
            }
            return a.name.localeCompare(b.name)
        })
    }, [districts])

    const filteredDistricts = useMemo(() => {
        if (!searchTerm.trim()) {
            return normalizedDistricts
        }
        const query = searchTerm.trim().toLowerCase()
        return normalizedDistricts.filter((district) =>
            district.name.toLowerCase().includes(query)
        )
    }, [normalizedDistricts, searchTerm])

    const toggleDistrict = (districtId: number) => {
        setSelectedDistricts((prev) => {
            if (prev.includes(districtId)) {
                return prev.filter((id) => id !== districtId)
            }
            return [...prev, districtId]
        })
    }

    const handleSave = async () => {
        if (selectedDistricts.length === 0) {
            toast.error('Selecciona al menos un distrito donde atiendes.')
            return
        }

        setSaving(true)
        try {
            await configureProfile.mutateAsync({
                district_ids: selectedDistricts,
            })

            toast.success('Cobertura guardada correctamente')
            onComplete?.()
        } catch (error) {
            console.error('Error saving districts:', error)
            toast.error('No se pudo guardar la cobertura. Intenta nuevamente.')
        } finally {
            setSaving(false)
        }
    }

    if (providerLoading || districtsLoading) {
        return <div className="step-loading">Cargando información...</div>
    }

    return (
        <div className="step-container">
            <div className="step-section">
                <h4 className="step-section-title">📍 Distritos donde trabajas</h4>
                <p className="step-hint">
                    Selecciona todos los distritos en los que puedes brindar tus servicios. Mientras más opciones, más clientes potenciales te encontrarán.
                </p>

                <div className="step-district-toolbar">
                    <div className="step-district-search">
                        <Input
                            name="districtSearch"
                            placeholder="Buscar distrito..."
                            value={searchTerm}
                            setValue={setSearchTerm}
                        />
                    </div>
                    <span className="step-selection-pill">
                        {selectedDistricts.length} distrito{selectedDistricts.length === 1 ? '' : 's'} seleccionado{selectedDistricts.length === 1 ? '' : 's'}
                    </span>
                </div>

                <div className="step-district-grid">
                    {filteredDistricts.map((district: District) => {
                        const isSelected = selectedDistricts.includes(district.id)
                        return (
                            <button
                                key={district.id}
                                type="button"
                                className={`step-district-chip ${isSelected ? 'step-district-chip--selected' : ''}`}
                                onClick={() => toggleDistrict(district.id)}
                            >
                                <span className="step-district-name">{district.name}</span>
                                {/* <span className="step-district-multiplier">x{Number(district.price_multiplier ?? 1).toFixed(2)}</span> */}
                            </button>
                        )
                    })}
                </div>

                {filteredDistricts.length === 0 && (
                    <div className="step-empty-selection">
                        <p>No encontramos distritos con ese nombre.</p>
                        <p style={{ marginTop: '0.5rem' }}>Intenta con otra palabra clave.</p>
                    </div>
                )}
            </div>

            {/* <div className="step-tips">
                <h5 className="step-tips-title">💡 Consejos</h5>
                <div className="step-tip">• Marca únicamente los distritos donde realmente puedes atender sin retrasos.</div>
                <div className="step-tip">• Actualiza tu cobertura cuando agregues nuevas zonas o reduzcas tu radio de trabajo.</div>
            </div> */}

            <div className="step-actions">
                <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Guardando...' : 'Guardar cobertura'}
                </Button>
            </div>
        </div>
    )
}

export default LocationStep
